import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Phone, Mail, MessageCircle, MapPin, Instagram, Send } from 'lucide-react';
import { toast } from 'sonner';
import { mockData } from '../mock';

const ContactSection = () => {
  const { contact, uniformTypes } = mockData;
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const [formData, setFormData] = useState({
    companyName: '',
    contactPerson: '',
    phone: '',
    email: '',
    quantity: '',
    uniformType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData((prev) => ({ ...prev, uniformType: value }));
  };

  const openWhatsAppWithQuote = (data) => {
    const message = `New Quote Request!\n\nCompany: ${data.companyName}\nContact: ${data.contactPerson}\nPhone: ${data.phone}\nEmail: ${data.email || 'N/A'}\nQuantity: ${data.quantity} pieces\nType: ${data.uniformType}\nMessage: ${data.message || 'N/A'}`;
    window.location.href = `https://wa.me/${contact.whatsapp.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(message)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const res = await fetch(`${API_URL}/api/contact/quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_name: formData.companyName,
          contact_person: formData.contactPerson,
          phone: formData.phone,
          email: formData.email,
          quantity: formData.quantity,
          uniform_type: formData.uniformType,
          message: formData.message
        })
      });

      if (!res.ok) throw new Error('Failed to submit');

      toast.success('Quote request submitted! Redirecting to WhatsApp...');

      // Open WhatsApp with quote details after short delay
      const savedData = { ...formData };
      setFormData({
        companyName: '', contactPerson: '', phone: '',
        email: '', quantity: '', uniformType: '', message: ''
      });

      setTimeout(() => openWhatsAppWithQuote(savedData), 1000);
    } catch (err) {
      toast.error('Something went wrong. Please try again or contact us on WhatsApp.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWhatsApp = () => {
    const message = `Hello! I'm interested in uniforms.\n\nCompany: ${formData.companyName}\nContact: ${formData.contactPerson}\nPhone: ${formData.phone}\nQuantity: ${formData.quantity}\nType: ${formData.uniformType}`;
    window.location.href = `https://wa.me/${contact.whatsapp.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(message)}`;
  };

  return (
    <section id="contact" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Get In Touch</h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Ready to order? Contact us today for a free consultation and quote
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Contact Information</h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="h-6 w-6 text-blue-900" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">Phone</div>
                    <a href={`tel:${contact.phone}`} className="text-gray-600 hover:text-blue-900 transition-colors">
                      {contact.phone}
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MessageCircle className="h-6 w-6 text-blue-900" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">WhatsApp</div>
                    <a
                      href={`https://wa.me/${contact.whatsapp.replace(/[^0-9]/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-600 hover:text-blue-900 transition-colors"
                    >
                      {contact.whatsapp}
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="h-6 w-6 text-blue-900" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">Email</div>
                    <a
                      href={`mailto:${contact.email}`}
                      className="text-gray-600 hover:text-blue-900 transition-colors"
                    >
                      {contact.email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Instagram className="h-6 w-6 text-blue-900" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">Instagram</div>
                    <a
                      href={`https://instagram.com/${contact.instagram.replace('@', '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-600 hover:text-blue-900 transition-colors"
                    >
                      {contact.instagram}
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-6 w-6 text-blue-900" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">Location</div>
                    <div className="text-gray-600">OFFICE 23, FLOOR M1. ALI TOWER MIQAB QIBLA</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick WhatsApp Button */}
            <Card className="border-2 border-blue-900 bg-blue-50">
              <CardHeader>
                <CardTitle className="flex items-center text-blue-900">
                  <MessageCircle className="mr-2 h-5 w-5" />
                  Prefer WhatsApp?
                </CardTitle>
                <CardDescription>Get instant response from our team</CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  onClick={handleWhatsApp}
                  className="w-full bg-blue-900 hover:bg-blue-800 text-white"
                  size="lg"
                >
                  Chat on WhatsApp
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <Card className="border-2 border-gray-200">
            <CardHeader>
              <CardTitle className="text-2xl">Request a Quote</CardTitle>
              <CardDescription>Fill out the form and we'll get back to you within 24 hours</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="companyName">Company Name *</Label>
                  <Input
                    id="companyName"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleInputChange}
                    placeholder="Your company name"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contactPerson">Contact Person *</Label>
                  <Input
                    id="contactPerson"
                    name="contactPerson"
                    value={formData.contactPerson}
                    onChange={handleInputChange}
                    placeholder="Your name"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+965 XXXX XXXX"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="quantity">Quantity *</Label>
                    <Input
                      id="quantity"
                      name="quantity"
                      type="number"
                      value={formData.quantity}
                      onChange={handleInputChange}
                      placeholder="Number of pieces"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="uniformType">Uniform Type *</Label>
                    <Select onValueChange={handleSelectChange} value={formData.uniformType} required>
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {uniformTypes.map((type) => (
                          <SelectItem key={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">Additional Details</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Tell us about your requirements, logo needs, colors, etc."
                    rows={4}
                  />
                </div>

                <Button type="submit" disabled={isSubmitting} className="w-full bg-blue-900 hover:bg-blue-800 text-white" size="lg" data-testid="submit-quote-btn">
                  <Send className="mr-2 h-5 w-5" />
                  {isSubmitting ? 'Submitting...' : 'Submit Quote Request'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
